import React from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { 
  Coffee, Home, Building2, Calendar, DollarSign, 
  AlertCircle, CheckCircle, Clock, Users, MessageSquare,
  TrendingUp, Award, Brain, Pizza, Zap, Heart,
  Briefcase, Video, Phone, Mail, Mic, MicOff,
  Sun, Moon, Cloud, Umbrella, ThermometerSun,
  Activity, Battery, Wifi, WifiOff, Volume2,
  Palmtree, Pill, HelpCircle, Lightbulb, Gift,
  Car, LogOut, LogIn, Send, BarChart3
} from 'lucide-react';
import { useWorkStore } from '../../store/work-buttons/workStore';

interface ButtonConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  action: () => void;
  category: 'status' | 'request' | 'communication' | 'productivity' | 'wellness' | 'management';
  roles: ('employee' | 'manager')[];
  quickAccess?: boolean;
}

export function ButtonGrid({ userRole }: { userRole: 'employee' | 'manager' }) {
  const { addActivity, updateStatus } = useWorkStore();

  const handleButtonClick = (button: ButtonConfig) => {
    button.action();
    addActivity({
      id: Date.now().toString(),
      buttonId: button.id,
      label: button.label,
      timestamp: new Date().toISOString(),
      category: button.category
    });
  };

  const buttons: ButtonConfig[] = [
    // Status Updates
    {
      id: 'wfh',
      label: 'Working from Home',
      icon: <Home size={20} />,
      color: 'bg-blue-500',
      category: 'status',
      roles: ['employee', 'manager'],
      quickAccess: true,
      action: () => {
        updateStatus('location', 'home');
        toast.success('Status updated: Working from home 🏠');
      }
    },
    {
      id: 'office',
      label: 'In Office',
      icon: <Building2 size={20} />,
      color: 'bg-green-500',
      category: 'status',
      roles: ['employee', 'manager'],
      quickAccess: true,
      action: () => {
        updateStatus('location', 'office');
        toast.success('Status updated: In the office 🏢');
      }
    },
    {
      id: 'break',
      label: 'On Break',
      icon: <Coffee size={20} />,
      color: 'bg-yellow-500',
      category: 'status',
      roles: ['employee', 'manager'],
      quickAccess: true,
      action: () => {
        updateStatus('availability', 'break');
        toast('☕ Enjoy your break! Timer started.', {
          icon: '⏰',
          duration: 4000,
        });
        setTimeout(() => {
          toast('Break reminder: 5 minutes remaining', { icon: '⏰' });
        }, 10 * 60 * 1000); // 10 min reminder
      }
    },
    {
      id: 'meeting',
      label: 'In Meeting',
      icon: <Video size={20} />,
      color: 'bg-purple-500',
      category: 'status',
      roles: ['employee', 'manager'],
      action: () => {
        updateStatus('availability', 'meeting');
        toast('Status: In meeting 🔴', { icon: '📹' });
      }
    },
    {
      id: 'focus',
      label: 'Focus Time',
      icon: <Brain size={20} />,
      color: 'bg-indigo-500',
      category: 'productivity',
      roles: ['employee', 'manager'],
      action: () => {
        updateStatus('availability', 'focus');
        toast('🧠 Focus mode activated. Notifications muted.', {
          icon: '🎯',
          duration: 5000,
        });
      }
    },

    // Requests
    {
      id: 'sick',
      label: 'Sick Day',
      icon: <Pill size={20} />,
      color: 'bg-red-500',
      category: 'request',
      roles: ['employee'],
      action: () => {
        toast.success('Sick day requested. Get well soon! 🤧');
        // Send notification to manager
      }
    },
    {
      id: 'pto',
      label: 'Request Time Off',
      icon: <Palmtree size={20} />,
      color: 'bg-teal-500',
      category: 'request',
      roles: ['employee'],
      action: () => {
        toast.success('PTO request form opened 🏖️');
        // Open PTO request modal
      }
    },
    {
      id: 'expense',
      label: 'Submit Expense',
      icon: <DollarSign size={20} />,
      color: 'bg-emerald-500',
      category: 'request',
      roles: ['employee', 'manager'],
      action: () => {
        toast('📸 Take a photo of your receipt', { icon: '💳' });
        // Open expense submission
      }
    },
    {
      id: 'help',
      label: 'Need Help',
      icon: <HelpCircle size={20} />,
      color: 'bg-orange-500',
      category: 'communication',
      roles: ['employee'],
      action: () => {
        toast('🆘 Help request sent to available team members');
      }
    },

    // Communication
    {
      id: 'standup',
      label: 'Daily Standup Done',
      icon: <Mic size={20} />,
      color: 'bg-cyan-500',
      category: 'communication',
      roles: ['employee', 'manager'],
      action: () => {
        toast.success('✅ Daily standup marked complete');
      }
    },
    {
      id: 'update',
      label: 'Send Status Update',
      icon: <Send size={20} />,
      color: 'bg-blue-600',
      category: 'communication',
      roles: ['employee', 'manager'],
      action: () => {
        toast('📝 Quick update sent to team');
      }
    },
    {
      id: 'idea',
      label: 'Submit Idea',
      icon: <Lightbulb size={20} />,
      color: 'bg-yellow-600',
      category: 'productivity',
      roles: ['employee', 'manager'],
      action: () => {
        toast('💡 Great idea! Submitted to innovation board');
      }
    },

    // Management (Manager only)
    {
      id: 'approve',
      label: 'Approve Pending',
      icon: <CheckCircle size={20} />,
      color: 'bg-green-600',
      category: 'management',
      roles: ['manager'],
      action: () => {
        toast.success('✅ All pending items approved');
      }
    },
    {
      id: 'announce',
      label: 'Team Announcement',
      icon: <Volume2 size={20} />,
      color: 'bg-purple-600',
      category: 'management',
      roles: ['manager'],
      action: () => {
        toast('📢 Announcement sent to all channels');
      }
    },
    {
      id: 'recognition',
      label: 'Give Recognition',
      icon: <Award size={20} />,
      color: 'bg-pink-500',
      category: 'management',
      roles: ['manager'],
      action: () => {
        toast('🏆 Recognition sent! +50 points awarded');
      }
    },
    {
      id: 'metrics',
      label: 'Team Metrics',
      icon: <BarChart3 size={20} />,
      color: 'bg-indigo-600',
      category: 'management',
      roles: ['manager'],
      action: () => {
        toast('📊 Loading team dashboard...');
      }
    },

    // Wellness
    {
      id: 'lunch',
      label: 'Order Lunch',
      icon: <Pizza size={20} />,
      color: 'bg-red-600',
      category: 'wellness',
      roles: ['employee', 'manager'],
      action: () => {
        toast('🍕 Joined group lunch order!');
      }
    },
    {
      id: 'birthday',
      label: 'Birthday Reminder',
      icon: <Gift size={20} />,
      color: 'bg-pink-600',
      category: 'wellness',
      roles: ['employee', 'manager'],
      action: () => {
        toast("🎂 It's Sarah's birthday today! Card sent.");
      }
    },
    {
      id: 'leaving',
      label: 'Leaving Early',
      icon: <Car size={20} />,
      color: 'bg-gray-600',
      category: 'status',
      roles: ['employee', 'manager'],
      action: () => {
        toast('🚗 Team notified: Leaving early today');
      }
    },
    {
      id: 'clock-out',
      label: 'Clock Out',
      icon: <LogOut size={20} />,
      color: 'bg-slate-600',
      category: 'status',
      roles: ['employee', 'manager'],
      action: () => {
        const time = new Date().toLocaleTimeString();
        toast.success(`Clocked out at ${time}. Have a great evening! 🌙`);
      }
    }
  ];

  // Filter buttons based on role
  const availableButtons = buttons.filter(b => b.roles.includes(userRole));
  const quickButtons = availableButtons.filter(b => b.quickAccess);
  const otherButtons = availableButtons.filter(b => !b.quickAccess);

  // Group buttons by category
  const categories = {
    status: otherButtons.filter(b => b.category === 'status'),
    request: otherButtons.filter(b => b.category === 'request'),
    communication: otherButtons.filter(b => b.category === 'communication'),
    productivity: otherButtons.filter(b => b.category === 'productivity'),
    wellness: otherButtons.filter(b => b.category === 'wellness'),
    management: otherButtons.filter(b => b.category === 'management'),
  };

  return (
    <div className="space-y-8">
      {/* Quick Access Buttons */}
      {quickButtons.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {quickButtons.map((button, index) => (
              <WorkButton
                key={button.id}
                button={button}
                onClick={() => handleButtonClick(button)}
                delay={index * 0.05}
                isQuick={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Categorized Buttons */}
      {Object.entries(categories).map(([category, buttons]) => {
        if (buttons.length === 0) return null;
        
        return (
          <div key={category}>
            <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4 capitalize">
              {category}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {buttons.map((button, index) => (
                <WorkButton
                  key={button.id}
                  button={button}
                  onClick={() => handleButtonClick(button)}
                  delay={index * 0.05}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface WorkButtonProps {
  button: ButtonConfig;
  onClick: () => void;
  delay: number;
  isQuick?: boolean;
}

function WorkButton({ button, onClick, delay, isQuick }: WorkButtonProps) {
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`
        relative overflow-hidden group
        ${isQuick ? 'p-6' : 'p-5'}
        ${button.color} text-white rounded-xl shadow-lg
        hover:shadow-xl transition-all duration-200
        flex flex-col items-center justify-center gap-2
      `}
    >
      <motion.div
        initial={{ rotate: 0 }}
        whileHover={{ rotate: [0, -10, 10, -10, 0] }}
        transition={{ duration: 0.5 }}
      >
        {button.icon}
      </motion.div>
      <span className="text-sm font-medium text-center">
        {button.label}
      </span>
      
      {/* Ripple effect on click */}
      <motion.div
        className="absolute inset-0 bg-white opacity-0"
        whileHover={{ opacity: 0.1 }}
        whileTap={{ opacity: 0.3 }}
      />
    </motion.button>
  );
}